package org.catharsis.spendtel.feature.presentation.auth

import androidx.lifecycle.viewModelScope

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import org.catharsis.spendtel.core.api.AnalyticsApi
import org.catharsis.spendtel.core.api.AuthenticationRepo
import org.catharsis.spendtel.core.auth.authErrorMessage
import org.catharsis.spendtel.core.auth.GoogleSignInProvider
import org.catharsis.spendtel.core.auth.isGoogleSignInCancelled
import org.catharsis.spendtel.core.arch.MviViewModel
import org.catharsis.spendtel.core.arch.ViewEffect
import org.catharsis.spendtel.core.arch.ViewEvent
import org.catharsis.spendtel.core.arch.ViewState
import org.catharsis.spendtel.feature.data.EventName
import org.catharsis.spendtel.feature.data.ScreenName
import org.catharsis.spendtel.feature.data.SpendtelUser

class AuthenticationViewModel(
    private val authenticationRepo: AuthenticationRepo,
    private val analytics: AnalyticsApi,
): MviViewModel<AuthenticationEvent, AuthenticationState, AuthenticationEffect>(
initialState = AuthenticationState()
){
    private val _authState: MutableStateFlow<AuthenticationState> = MutableStateFlow(value = AuthenticationState())
    var authState = _authState.asStateFlow()

    init {
        viewModelScope.launch {
            authenticationRepo.authSessionChanges().collect {
                refreshAuthState()
            }
        }
    }

    private suspend fun refreshAuthState() {
        val user = if (authenticationRepo.isAuthenticated) {
            authenticationRepo.getCurrentUser()
        } else {
            null
        }

        _authState.update {
            it.copy(
                isAuthenticated = authenticationRepo.isAuthenticated,
                currentUser = user,
                isInitialized = true,
            )
        }
    }

    suspend fun getCurrentUser(): SpendtelUser? {
        return authenticationRepo.getCurrentUser()
    }

    fun refreshCurrentUser() {
        viewModelScope.launch {
            refreshAuthState()
        }
    }

    override fun receiveEvent(event: AuthenticationEvent) {
        when (event) {
            is AuthenticationEvent.OnSignIn -> {
                signIn(event.email, event.password)
            }
            is AuthenticationEvent.OnRegister -> createAccount(event.email, event.password)
            is AuthenticationEvent.OnForgotPassword -> forgotPassword(event.email)
            is AuthenticationEvent.OnUpdateProfilePic -> {}
            AuthenticationEvent.OnGoogleSignIn -> signInWithGoogle()
            AuthenticationEvent.OnSignOut -> signOut()
            AuthenticationEvent.OnDeleteAccount -> deleteAccount()
        }
    }

    private fun createAccount(email: String, password: String) {
        viewModelScope.launch {
            toggleLoadingState(loading = true)
            val result = authenticationRepo.createAccount(email, password)
                .onFailure {
                    emitEffect {
                        AuthenticationEffect.OnRegisterError(authErrorMessage(it))
                    }
                }

            toggleLoadingState(loading = false)
            if (result.isSuccess) {
                val user = result.getOrNull()
                updateStateAfterAuth(user)
                emitEffect {
                    AuthenticationEffect.OnAuthSuccess(user)
                }
            }
        }
    }

    private fun signIn(email: String, password: String) {
        viewModelScope.launch {
            toggleLoadingState(loading = true)
            val result = authenticationRepo.signIn(email, password)
                .onFailure {
                    emitEffect {
                        AuthenticationEffect.OnSignInError(message = authErrorMessage(it))
                    }
                }
            toggleLoadingState(loading = false)

            if (result.isSuccess) {
                val user = result.getOrNull()
                updateStateAfterAuth(user)
                emitEffect {
                    AuthenticationEffect.OnAuthSuccess(user)
                }
            }
        }
    }

    private fun signInWithGoogle() {
        viewModelScope.launch {
            toggleLoadingState(loading = true)
            try {
                val tokenResult = GoogleSignInProvider.getGoogleTokens()
                if (tokenResult.isFailure) {
                    val error = tokenResult.exceptionOrNull()
                    if (!isGoogleSignInCancelled(error)) {
                        emitEffect {
                            AuthenticationEffect.OnSignInError(
                                error?.message ?: "Google sign-in failed. Please try again.",
                            )
                        }
                    }
                    return@launch
                }

                val tokens = tokenResult.getOrThrow()
                val result = authenticationRepo.signInWithGoogle(tokens.idToken, tokens.accessToken)
                    .onFailure {
                        emitEffect {
                            AuthenticationEffect.OnSignInError(
                                it.message ?: "Google sign-in failed. Please try again.",
                            )
                        }
                    }

                if (result.isSuccess) {
                    val user = result.getOrNull()
                    updateStateAfterAuth(user)
                    emitEffect { AuthenticationEffect.OnAuthSuccess(user) }
                }
            } finally {
                toggleLoadingState(loading = false)
            }
        }
    }

    private fun deleteAccount() {
        viewModelScope.launch {
            _authState.update { it.copy(isDeletingAccount = true) }
            val result = authenticationRepo.deleteAccount()
            if (result.isSuccess) {
                refreshAuthState()
                emitEffect {
                    AuthenticationEffect.OnDeleteAccountResult(
                        success = true,
                        message = "Your account and uploaded statements were deleted.",
                    )
                }
            } else {
                emitEffect {
                    AuthenticationEffect.OnDeleteAccountResult(
                        success = false,
                        message = result.exceptionOrNull()?.message
                            ?: "Could not delete your account. Please try again.",
                    )
                }
            }
            _authState.update { it.copy(isDeletingAccount = false) }
        }
    }

    private fun signOut() {
        viewModelScope.launch {
            toggleLoadingState(loading = true)
            val result = authenticationRepo.signOut()

            toggleLoadingState(loading = false)
            emitEffect { AuthenticationEffect.OnSignOutRequestUpdate(success = result.isSuccess) }


        }
    }

    private fun forgotPassword(email: String) {
        viewModelScope.launch {
            toggleLoadingState(loading = true)
            val result = authenticationRepo.forgotPassword(email)
            toggleLoadingState(loading = false)
            emitEffect {
                AuthenticationEffect.OnForgotPasswordRequestUpdate(success = result.isSuccess)
            }
        }
    }

    private suspend fun updateStateAfterAuth(user: SpendtelUser?) {
        _authState.update {
            it.copy(
                isAuthenticated = authenticationRepo.isAuthenticated,
                currentUser = user ?: authenticationRepo.getCurrentUser(),
                isInitialized = true,
            )
        }
    }

    private fun toggleLoadingState(loading: Boolean) {
        _authState.update { it.copy(isLoading = loading) }
    }

    private fun updateState(newState: AuthenticationState) {
        viewModelScope.launch {
            _authState.emit(newState)
        }
    }

    fun logScreenViewed(screenName: ScreenName) {
        analytics.logScreenViewed(screenName)
    }

    fun logClickEvent(eventName: EventName, extras: Map<String, Any>? = null) {
        analytics.logClickEvent(eventName, extras)
    }
}

data class AuthenticationState(
    val isAuthenticated: Boolean = false,
    val currentUser: SpendtelUser? = null,
    val isLoading: Boolean = false,
    val isDeletingAccount: Boolean = false,
    val isInitialized: Boolean = false, // True once initial auth check is complete
): ViewState

sealed class AuthenticationEvent: ViewEvent {
    data class OnSignIn(val email: String, val password: String) : AuthenticationEvent()
    data class OnRegister(val email: String, val password: String) : AuthenticationEvent()
    data object OnGoogleSignIn : AuthenticationEvent()
    data object OnSignOut: AuthenticationEvent()
    data object OnDeleteAccount: AuthenticationEvent()
    data class OnForgotPassword(val email: String) : AuthenticationEvent()
    data class OnUpdateProfilePic(val picBytes: ByteArray) : AuthenticationEvent()
}

sealed class AuthenticationEffect: ViewEffect {
    data class OnAuthSuccess(val user: SpendtelUser?) : AuthenticationEffect()
    data class OnSignInError(val message: String): AuthenticationEffect()

    data class OnRegisterError(val message: String): AuthenticationEffect()
    data class OnSignOutRequestUpdate(val success: Boolean): AuthenticationEffect()
    data class OnForgotPasswordRequestUpdate(val success: Boolean): AuthenticationEffect()
    data class OnDeleteAccountResult(val success: Boolean, val message: String): AuthenticationEffect()
}